/** 两个渠道独立拥有交付状态。 */
export type AnalyticsChannel = 'sensors' | 'snowplow';
/** 只有pending允许再次交付，调用后的异常必须终结。 */
export type ChannelResult = 'pending' | 'handed-off' | 'uncertain' | 'rejected';
/** 记录只暴露元数据；业务快照通过闭包保存。 */
export interface PendingAnalyticsRecord {
  id: string;
  capturedAt: number;
  channels: readonly AnalyticsChannel[];
  deliver(channel: AnalyticsChannel): Promise<ChannelResult>;
  /** 完成、过期或销毁时释放尚未导入SDK的上下文引用。 */
  onRemove?(reason: 'completed' | 'expired' | 'disposed'): void;
}
/** 页面生命周期有界交付入口。 */
export interface AnalyticsDeliveryQueue {
  enqueue(record: PendingAnalyticsRecord): boolean;
  /** 生命周期回调内立即交付指定记录，不等待定时器或微任务。 */
  deliverNow(id: string): void;
  wake(): void;
  /** 离页回调内立即开始交付，不等待定时器或普通批次预算。 */
  flushNow(): void;
  /** 仅统计未过期、未终态渠道，供初始化恢复判断需求。 */
  hasPending(channel: AnalyticsChannel): boolean;
  dispose(): void;
}
/** 固定容量与最长保留时间，不记录payload。 */
const MAX_PENDING_RECORDS = 1000;
const RECORD_TTL_MS = 120_000;
const RETRY_DELAYS_MS = [1000, 2000, 5000, 10_000] as const;
/** 限制单轮同步投递，给压缩、网络和销毁任务留下事件循环时间。 */
const MAX_CHANNEL_STARTS_PER_TURN = 50;
/** 批次结束后明确让出一个短窗口，避免零延时续扫长期抢占流式压缩。 */
const TURN_YIELD_MS = 16;
/** 每个渠道独立重试与inflight。 */
interface ChannelState {
  inflight: boolean;
  done: boolean;
  attempts: number;
  nextAt: number;
}
/** 运行中的记录与渠道状态。 */
interface Entry {
  record: PendingAnalyticsRecord;
  states: Map<AnalyticsChannel, ChannelState>;
}

/** 创建不依赖React或SDK的有界队列；构造不启动定时器。 */
export function createAnalyticsDeliveryQueue(options: {
  now?: () => number;
  onDiagnostic: (
    code: string,
    metadata: Readonly<Record<string, number | string | boolean>>,
  ) => void;
}): AnalyticsDeliveryQueue {
  const now = options.now ?? Date.now;
  const entries = new Map<string, Entry>();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let disposed = false;
  /** 同步交付回调内的重入wake不能反复清除旧pending退避。 */
  let delivering = false;
  /** wake/settle只改变渠道到期时间，不能缩短已经建立的让出窗口。 */
  let nextTurnAt = 0;

  /** 统一释放记录，迟到Promise不能重新加入。 */
  function remove(id: string, reason: 'completed' | 'expired' | 'disposed'): void {
    const entry = entries.get(id);
    entries.delete(id);
    entry?.record.onRemove?.(reason);
  }

  /** 下次定时器只关注最近的TTL或可重试渠道。 */
  function schedule(): void {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    if (disposed || entries.size === 0) return;
    let nextAt = Infinity;
    for (const { record, states } of Array.from(entries.values())) {
      nextAt = Math.min(nextAt, record.capturedAt + RECORD_TTL_MS);
      for (const state of Array.from(states.values())) {
        if (!state.done && !state.inflight) nextAt = Math.min(nextAt, state.nextAt);
      }
    }
    timer = setTimeout(flush, Math.max(0, Math.max(nextAt, nextTurnAt) - now()));
  }

  /** 单渠道结果独立落定，不让旧pending串行阻塞其余记录。 */
  function settle(entry: Entry, state: ChannelState, result: ChannelResult): void {
    if (disposed || entries.get(entry.record.id) !== entry) return;
    state.inflight = false;
    state.done = result !== 'pending';
    state.nextAt = now() + RETRY_DELAYS_MS[Math.min(state.attempts++, RETRY_DELAYS_MS.length - 1)];
    if (Array.from(entry.states.values()).every((item) => item.done))
      remove(entry.record.id, 'completed');
    schedule();
  }

  /** 遍历按插入顺序执行，但每次交付独立等待。 */
  function flush(immediate = false): void {
    timer = undefined;
    if (disposed) return;
    const channels = Array.from(entries.values()).flatMap((entry) =>
      Array.from(entry.states).map(([channel, state]) => ({
        entry,
        channel,
        state,
      })),
    );
    let started = 0;
    for (const { entry, channel, state } of channels) {
      if (entries.get(entry.record.id) !== entry) continue;
      if (now() - entry.record.capturedAt >= RECORD_TTL_MS) {
        options.onDiagnostic('record_expired', { channels: entry.states.size });
        remove(entry.record.id, 'expired');
        continue;
      }
      if (state.done || state.inflight || (!immediate && state.nextAt > now())) continue;
      state.inflight = true;
      /** 立即模式在当前生命周期调用栈进入 SDK，异常仍由渠道状态管理。 */
      const deliver = (): Promise<ChannelResult> | ChannelResult => {
        if (disposed || entries.get(entry.record.id) !== entry) return 'rejected' as const;
        delivering = true;
        try {
          return entry.record.deliver(channel);
        } catch {
          return 'uncertain';
        } finally {
          delivering = false;
        }
      };
      // 普通交付保留原有让出策略；离页只异步处理结果，不异步启动交付。
      (immediate ? Promise.resolve(deliver()) : Promise.resolve().then(deliver)).then(
        (result) => settle(entry, state, result),
        () => settle(entry, state, 'uncertain'),
      );
      if (!immediate && ++started >= MAX_CHANNEL_STARTS_PER_TURN) {
        // 本轮同步dispatch微任务之后再计时；重入wake和迟到settle不能取消此窗口。
        void Promise.resolve().then(() => {
          if (disposed) return;
          nextTurnAt = now() + TURN_YIELD_MS;
          schedule();
        });
        return;
      }
    }
    schedule();
  }

  return {
    flushNow() {
      if (timer !== undefined) clearTimeout(timer);
      flush(true);
    },
    /** 同步报告容量接纳，首次交付始终异步。 */
    enqueue(record) {
      if (disposed || entries.has(record.id) || now() - record.capturedAt >= RECORD_TTL_MS)
        return false;
      if (entries.size >= MAX_PENDING_RECORDS) {
        options.onDiagnostic('queue_overflow', { count: entries.size });
        return false;
      }
      entries.set(record.id, {
        record,
        states: new Map(
          record.channels.map((channel) => [
            channel,
            { inflight: false, done: false, attempts: 0, nextAt: now() },
          ]),
        ),
      });
      schedule();
      return true;
    },
    /** 仅离页记录越过调度窗口，仍共用inflight、TTL和终态去重。 */
    deliverNow(id) {
      const entry = entries.get(id);
      if (disposed || !entry) return;
      if (now() - entry.record.capturedAt >= RECORD_TTL_MS) {
        remove(id, 'expired');
        return;
      }
      for (const [channel, state] of Array.from(entry.states)) {
        if (disposed || entries.get(id) !== entry) return;
        if (state.done || state.inflight) continue;
        state.inflight = true;
        const wasDelivering = delivering;
        delivering = true;
        try {
          // 先同步调用SDK，Promise仅用于观察交付结果。
          Promise.resolve(entry.record.deliver(channel)).then(
            (result) => settle(entry, state, result),
            () => settle(entry, state, 'uncertain'),
          );
        } catch {
          settle(entry, state, 'uncertain');
        } finally {
          delivering = wasDelivering;
        }
      }
    },
    /** 外部就绪信号可提前唤醒pending，但不能重入inflight或终态。 */
    wake() {
      if (disposed) return;
      if (!delivering)
        for (const entry of Array.from(entries.values()))
          for (const state of Array.from(entry.states.values())) state.nextAt = now();
      schedule();
    },
    /** 读取有效需求不修改队列或延长TTL。 */
    hasPending(channel) {
      return Array.from(entries.values()).some(
        ({ record, states }) =>
          now() - record.capturedAt < RECORD_TTL_MS &&
          states.has(channel) &&
          !states.get(channel)!.done,
      );
    },
    /** 终态清理计时器及全部闭包。 */
    dispose() {
      disposed = true;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      for (const id of Array.from(entries.keys())) remove(id, 'disposed');
    },
  };
}
